import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import {
  Video, RefreshCw, Settings, AlertTriangle, CheckCircle2, Plus, Trash2,
  ShieldAlert, X, CalendarDays, Wrench,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Can from '../components/Can';
import { useAuth } from '../contexts/AuthContext';

const pad = (n) => String(n).padStart(2, '0');
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const thisMonth = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`; };
const nowTime = () => new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const fmtDay = (ds) => { const [y, m, d] = ds.split('-').map(Number); return WEEKDAY[new Date(y, m - 1, d).getDay()]; };

const blankForm = (date) => ({ id: null, checkDate: date, checkTimeText: '', checkedBy: '', allFunctional: true, issues: '' });

export default function Surveillance() {
  const { can } = useAuth();
  const canOperate = can('surveillance:operate');

  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(blankForm(todayStr()));
  const [saving, setSaving] = useState(false);

  const [openOut, setOpenOut] = useState([]);
  const [outForm, setOutForm] = useState({ camera: '', outageDate: todayStr(), downTimeText: '', restoredTimeText: '', itNotified: false, notes: '' });

  const [config, setConfig] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [cfgForm, setCfgForm] = useState(null);
  const [admins, setAdmins] = useState([]);

  const [y, m] = month.split('-').map(Number);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [monRes, todRes, openRes, cfgRes] = await Promise.all([
        api.get('/admin/surveillance/month', { params: { year: y, month: m } }),
        api.get('/admin/surveillance/today'),
        api.get('/admin/surveillance/outages/open'),
        api.get('/admin/surveillance/config'),
      ]);
      setData(monRes.data);
      setToday(todRes.data);
      setOpenOut(openRes.data.outages || []);
      setConfig(cfgRes.data.config);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load surveillance log');
    } finally { setLoading(false); }
  }, [y, m]);

  useEffect(() => { load(); }, [load]);

  const selectDay = async (day) => {
    if (day.hasCheck && day.check) {
      try {
        const { data: d } = await api.get(`/admin/surveillance/checks/${day.check.id}`);
        setForm({
          id: d.check.id, checkDate: d.check.checkDate,
          checkTimeText: d.check.checkTimeText || '', checkedBy: d.check.checkedBy || '',
          allFunctional: !!d.check.allFunctional, issues: d.check.issues || '',
        });
      } catch { toast.error('Failed to load check'); }
    } else {
      setForm(blankForm(day.date));
    }
  };

  const saveCheck = async () => {
    if (!form.checkDate) { toast.error('Pick a date'); return; }
    setSaving(true);
    try {
      const payload = {
        checkDate: form.checkDate,
        checkTimeText: form.checkTimeText || nowTime(),
        checkedBy: form.checkedBy || null,
        allFunctional: form.allFunctional,
        issues: form.issues || null,
      };
      if (form.id) await api.put(`/admin/surveillance/checks/${form.id}`, payload);
      else await api.post('/admin/surveillance/checks', payload);
      toast.success(form.id ? 'Check updated' : 'Check logged');
      setForm(blankForm(todayStr()));
      load();
    } catch (e) { toast.error(e?.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const deleteCheck = async (id) => {
    try { await api.delete(`/admin/surveillance/checks/${id}`); toast.success('Deleted'); if (form.id === id) setForm(blankForm(todayStr())); load(); }
    catch { toast.error('Delete failed'); }
  };

  const logToday = () => { setForm(blankForm(todayStr())); setTimeout(() => saveCheckToday(), 0); };
  const saveCheckToday = async () => {
    try {
      await api.post('/admin/surveillance/checks', { checkDate: todayStr(), checkTimeText: nowTime(), allFunctional: true });
      toast.success('Today logged');
      load();
    } catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
  };

  const addOutage = async (e) => {
    if (e) e.preventDefault();
    if (!outForm.camera.trim()) { toast.error('Enter a camera'); return; }
    try {
      await api.post('/admin/surveillance/outages', outForm);
      toast.success('Outage logged');
      setOutForm({ camera: '', outageDate: todayStr(), downTimeText: '', restoredTimeText: '', itNotified: false, notes: '' });
      load();
    } catch (e2) { toast.error(e2?.response?.data?.error || 'Failed'); }
  };
  const restoreOutage = async (id) => {
    try { await api.post(`/admin/surveillance/outages/${id}/restore`, { restoredTimeText: nowTime() }); toast.success('Marked restored'); load(); }
    catch { toast.error('Failed'); }
  };

  const openSettings = async () => {
    setCfgForm({ ...config, camerasText: (config.cameras || []).join(', ') });
    setShowConfig(true);
    try { const { data: a } = await api.get('/admin/admins'); setAdmins(a.admins || a.users || a || []); } catch { /* optional */ }
  };
  const saveSettings = async () => {
    try {
      const payload = {
        reminderEnabled: cfgForm.reminderEnabled,
        reminderTime: cfgForm.reminderTime,
        reminderRecipientUserIds: cfgForm.reminderRecipientUserIds || [],
        cameras: (cfgForm.camerasText || '').split(',').map((s) => s.trim()).filter(Boolean),
      };
      const { data: d } = await api.put('/admin/surveillance/config', payload);
      setConfig(d.config); setShowConfig(false); toast.success('Settings saved'); load();
    } catch (e) { toast.error(e?.response?.data?.error || 'Save failed'); }
  };

  const counts = (data && data.counts) || {};
  const days = (data && data.days) || [];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Video className="text-amber-400" size={22} />
          <h1 className="text-xl font-semibold text-gray-100">Surveillance Camera Check Log</h1>
        </div>
        <div className="flex items-center gap-2">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-gray-100" />
          <button onClick={load} className="p-2 rounded bg-gray-800 border border-gray-700 text-gray-300 hover:text-white" title="Refresh"><RefreshCw size={16} /></button>
          <Can permission="surveillance:configure">
            <button onClick={openSettings} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-gray-800 border border-gray-700 text-sm text-gray-300 hover:text-white"><Settings size={15} /> Settings</button>
          </Can>
        </div>
      </div>

      {/* Today banner */}
      {today && (
        today.checked ? (
          <div className="rounded-lg border border-emerald-800 bg-emerald-950/40 p-3 mb-4 flex items-center gap-2 text-emerald-200 text-sm">
            <CheckCircle2 size={18} className="text-emerald-400" />
            Today's check is logged{today.check?.checkTimeText ? ` at ${today.check.checkTimeText}` : ''}{today.check?.checkedBy ? ` by ${today.check.checkedBy}` : ''}.
          </div>
        ) : (
          <div className="rounded-lg border border-red-800 bg-red-950/50 p-3 mb-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-red-200 text-sm"><AlertTriangle size={18} className="text-red-400" /> Today's camera function check hasn't been logged yet.</div>
            {canOperate && <button onClick={logToday} className="px-3 py-1.5 rounded bg-red-600 hover:bg-red-500 text-white text-sm whitespace-nowrap">Log today — all functional</button>}
          </div>
        )
      )}

      {/* summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Tile label="Logged this month" value={counts.logged ?? 0} />
        <Tile label="Missing days" value={counts.missing ?? 0} accent={counts.missing ? 'red' : null} />
        <Tile label="Open outages" value={today ? today.openOutages : 0} accent={(today && today.openOutages) ? 'amber' : null} />
        <Tile label="Outages this month" value={counts.outages ?? 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Month log */}
        <div className="lg:col-span-2">
          {canOperate && (
            <div className="rounded-lg border border-gray-700 bg-gray-900 p-3 mb-3">
              <h3 className="text-sm font-semibold text-gray-200 mb-2">{form.id ? 'Edit check' : 'Log a check'}</h3>
              <div className="flex flex-wrap items-end gap-2">
                <Field label="Date" type="date" value={form.checkDate} onChange={(v) => setForm({ ...form, checkDate: v })} w="w-40" />
                <Field label="Time" value={form.checkTimeText} placeholder={nowTime()} onChange={(v) => setForm({ ...form, checkTimeText: v })} w="w-28" />
                <Field label="Checked by" value={form.checkedBy} placeholder="auto" onChange={(v) => setForm({ ...form, checkedBy: v })} w="w-40" />
                <label className="flex items-center gap-1.5 text-sm text-gray-300 mb-1.5">
                  <input type="checkbox" checked={form.allFunctional} onChange={(e) => setForm({ ...form, allFunctional: e.target.checked })} /> All functional
                </label>
                <button onClick={saveCheck} disabled={saving} className="px-4 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium disabled:opacity-60 mb-1">{form.id ? 'Update' : 'Log'}</button>
                {form.id && <button onClick={() => setForm(blankForm(todayStr()))} className="px-3 py-1.5 rounded border border-gray-700 text-gray-400 text-sm mb-1">New</button>}
              </div>
              <Field label="Issues" value={form.issues} placeholder="N/A" onChange={(v) => setForm({ ...form, issues: v })} w="w-full" className="mt-2" />
            </div>
          )}

          <div className="rounded-lg border border-gray-700 bg-gray-900 overflow-hidden">
            <div className="px-3 py-2 border-b border-gray-800 flex items-center gap-2 text-sm font-semibold text-gray-300">
              <CalendarDays size={15} /> {y}-{pad(m)} daily log
            </div>
            {loading ? <p className="p-4 text-gray-500 text-sm">Loading…</p> : (
              <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-900">
                    <tr className="text-left text-[11px] uppercase text-gray-500 border-b border-gray-800">
                      <th className="py-1.5 px-3">Date</th><th className="px-2">Status</th><th className="px-2">Time</th><th className="px-2">By</th><th className="px-2">Issues</th><th className="px-2">Out</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {days.map((d) => {
                      const missing = !d.hasCheck && !d.isFuture;
                      return (
                        <tr key={d.date} onClick={() => canOperate && selectDay(d)}
                          className={`border-b border-gray-800/60 ${canOperate ? 'cursor-pointer hover:bg-gray-800' : ''} ${form.checkDate === d.date ? 'bg-gray-800' : ''}`}>
                          <td className="py-1.5 px-3 text-gray-300 whitespace-nowrap">{d.date.slice(8)} <span className="text-gray-600">{fmtDay(d.date)}</span></td>
                          <td className="px-2">
                            {d.hasCheck ? <span className="text-emerald-400 text-xs">● Logged</span>
                              : d.isFuture ? <span className="text-gray-600 text-xs">—</span>
                              : <span className="text-red-400 text-xs font-medium">● Missing</span>}
                          </td>
                          <td className="px-2 text-gray-400">{d.check?.checkTimeText || ''}</td>
                          <td className="px-2 text-gray-400 truncate max-w-[120px]">{d.check?.checkedBy || ''}</td>
                          <td className="px-2 text-gray-400 truncate max-w-[220px]">{d.check && d.check.issues && d.check.issues !== 'N/A' ? d.check.issues : ''}</td>
                          <td className="px-2">{d.outageCount > 0 && <span className="text-amber-400 text-xs">{d.outageCount}</span>}</td>
                          <td className="px-2 text-right">{canOperate && d.hasCheck && <button onClick={(e) => { e.stopPropagation(); deleteCheck(d.check.id); }} className="text-gray-600 hover:text-red-400"><Trash2 size={13} /></button>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Outages */}
        <div className="lg:col-span-1 space-y-4">
          <div className="rounded-lg border border-gray-700 bg-gray-900 p-3">
            <h3 className="text-sm font-semibold text-gray-200 mb-2 flex items-center gap-1.5"><ShieldAlert size={15} className="text-amber-400" /> Open outages ({openOut.length})</h3>
            {openOut.length === 0 ? <p className="text-gray-500 text-sm">No cameras currently down.</p> : (
              <div className="space-y-2">
                {openOut.map((o) => (
                  <div key={o.id} className="rounded border border-amber-900/60 bg-amber-950/30 p-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-100 font-medium text-sm">{o.camera}</span>
                      {canOperate && <button onClick={() => restoreOutage(o.id)} className="text-xs px-2 py-0.5 rounded bg-emerald-700 hover:bg-emerald-600 text-white">Restore</button>}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-0.5">{o.outageDate}{o.downTimeText ? ` · down ${o.downTimeText}` : ''}{o.itNotified ? ' · IT notified' : ''}</div>
                    {o.notes && <div className="text-[11px] text-gray-500 mt-0.5">{o.notes}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {canOperate && (
            <div className="rounded-lg border border-gray-700 bg-gray-900 p-3">
              <h3 className="text-sm font-semibold text-gray-200 mb-2 flex items-center gap-1.5"><Wrench size={15} /> Log an outage</h3>
              <form onSubmit={addOutage} className="space-y-2">
                <input list="sv-cams" value={outForm.camera} placeholder="Camera / zone" onChange={(e) => setOutForm({ ...outForm, camera: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" />
                <datalist id="sv-cams">{(config?.cameras || []).map((c) => <option key={c} value={c} />)}</datalist>
                <div className="flex gap-2">
                  <input type="date" value={outForm.outageDate} onChange={(e) => setOutForm({ ...outForm, outageDate: e.target.value })} className="flex-1 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" />
                  <input value={outForm.downTimeText} placeholder="Down time" onChange={(e) => setOutForm({ ...outForm, downTimeText: e.target.value })} className="w-28 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" />
                </div>
                <div className="flex gap-2">
                  <input value={outForm.restoredTimeText} placeholder="Restored time (if known)" onChange={(e) => setOutForm({ ...outForm, restoredTimeText: e.target.value })} className="flex-1 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" />
                  <label className="flex items-center gap-1.5 text-xs text-gray-300"><input type="checkbox" checked={outForm.itNotified} onChange={(e) => setOutForm({ ...outForm, itNotified: e.target.checked })} /> IT notified</label>
                </div>
                <input value={outForm.notes} placeholder="Notes" onChange={(e) => setOutForm({ ...outForm, notes: e.target.value })} className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100" />
                <button type="submit" className="w-full flex items-center justify-center gap-1 px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white text-sm"><Plus size={15} /> Add outage</button>
              </form>
            </div>
          )}
        </div>
      </div>

      {showConfig && cfgForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-100">Surveillance settings</h2>
              <button onClick={() => setShowConfig(false)} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-200 mb-3">
              <input type="checkbox" checked={!!cfgForm.reminderEnabled} onChange={(e) => setCfgForm({ ...cfgForm, reminderEnabled: e.target.checked })} /> Daily reminder if today's check is unlogged
            </label>
            <Field label="Reminder time (for reference — set the actual schedule in Cloud Scheduler)" value={cfgForm.reminderTime || ''} onChange={(v) => setCfgForm({ ...cfgForm, reminderTime: v })} w="w-40" />
            <div className="mt-3">
              <span className="block text-[11px] text-gray-400 mb-1">Reminder recipients (push)</span>
              <div className="max-h-40 overflow-y-auto rounded border border-gray-700 bg-gray-800 p-2 space-y-1">
                {admins.length === 0 && <p className="text-xs text-gray-500">No admin list loaded.</p>}
                {admins.map((a) => {
                  const ids = cfgForm.reminderRecipientUserIds || [];
                  const checked = ids.includes(a.id);
                  return (
                    <label key={a.id} className="flex items-center gap-2 text-sm text-gray-200">
                      <input type="checkbox" checked={checked} onChange={(e) => {
                        const next = e.target.checked ? [...ids, a.id] : ids.filter((x) => x !== a.id);
                        setCfgForm({ ...cfgForm, reminderRecipientUserIds: next });
                      }} />
                      {(a.firstName || a.lastName) ? `${a.firstName || ''} ${a.lastName || ''}`.trim() : (a.email || a.id)}
                    </label>
                  );
                })}
              </div>
            </div>
            <Field label="Known cameras (comma-separated, for outage autocomplete)" value={cfgForm.camerasText || ''} onChange={(v) => setCfgForm({ ...cfgForm, camerasText: v })} w="w-full" className="mt-3" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowConfig(false)} className="px-4 py-1.5 rounded border border-gray-700 text-gray-300 text-sm">Cancel</button>
              <button onClick={saveSettings} className="px-4 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, accent }) {
  const color = accent === 'red' ? 'text-red-400' : accent === 'amber' ? 'text-amber-400' : 'text-gray-100';
  return (
    <div className="rounded-lg border border-gray-700 bg-gray-900 p-3">
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-gray-400">{label}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder, w = 'w-full', className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[11px] text-gray-400 mb-0.5">{label}</span>
      <input type={type} value={value ?? ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        className={`${w} bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-gray-100`} />
    </label>
  );
}
